import { Business, Service, ServiceSellCost } from "@turizi/shared";
import { useTranslation } from 'react-i18next';
import { Button, Modal, Select, SelectProps, Switch, Tooltip } from '@mantine/core';
import { MoneyNumberInput } from '../snippets';
import { useEffect, useState, useMemo } from 'react';
import { useCreateServiceSellCost } from "@/hooks/api-hooks";
import { DEFAULT_CURRENCY, IsPaxBased } from "@turizi/shared";
import { notify } from "@/lib/notify";
import { formatDay } from "@/lib/format-utils";
import { IconBuildingStore, IconCalendar, IconEdit, IconPlus } from "@tabler/icons-react";
import CurrencySelector from "../currency-selector";
import { TagIcon } from "@/lib/tag-utils";
import { DatePickerInput } from "@mantine/dates";
import dayjs from "dayjs";


interface Props {
    service: Service;
    cost: ServiceSellCost;
    label?: string;
}

export default function EditSingleCost({
    service,
    cost,
    label,
}: Props) {
    const { t, i18n } = useTranslation();
    const createCostMutation = useCreateServiceSellCost();
    const [draft, setDraft] = useState<ServiceSellCost>(cost);
    const [opened, setOpened] = useState(false);
    const [applyToAllProviders, setApplyToAllProviders] = useState(cost.business_id === null);
    const [showForeignerCost, setShowForeignerCost] = useState(false);

    const providerMap = useMemo(() => {
        return new Map((service?.providers || []).map((b: Business) => [b.id.toString(), b]));
    }, [service?.providers]);

    const selectedBusiness = useMemo(() => {
        if (!draft?.business_id) return null;
        return providerMap.get(draft.business_id.toString()) || null;
    }, [providerMap, draft?.business_id]);

    const renderProviderOption: SelectProps['renderOption'] = ({ option }) => {
        if (!option.value) {
            return (
                <div className="flex items-center gap-2 py-0.5">
                    <span className="text-gray-600 font-medium">{option.label}</span>
                </div>
            );
        }
        const b = providerMap.get(option.value);
        return (
            <div className="flex items-center gap-1.5 py-0.5 w-full">
                {b?.category && <TagIcon name={b.category} size="xxs" hideText />}
                <span className="font-medium text-sm text-gray-800">{option.label}</span>
                {b?.city && <span className="text-xs text-gray-400">{b.city}</span>}
                <span className="text-xxs text-gray-400">#{b?.id}</span>
            </div>
        );
    };

    const save = async () => {
        if (!service?.id) return;

        createCostMutation.mutate(draft, {
            onSuccess: () => {
                notify.success(t('cost_saved_successfully'), '');
                setOpened(false);
            }
        });
    };

    const costClassName = "p-2 bg-gray-50 rounded-lg flex items-center gap-1";

    useEffect(() => {
        setDraft(cost);
        setApplyToAllProviders(cost.business_id === null);
        setShowForeignerCost(Boolean(cost.foreigner_amount || cost.foreigner_currency));
    }, [cost]);

    const dateRangeLabel = `${cost.start_day ? formatDay(cost.start_day) : '—'} – ${cost.end_day ? formatDay(cost.end_day) : '—'}`;

    return (
        <>
            <Tooltip label={`${t('edit_cost')} (${dateRangeLabel})`} withArrow>
                <Button
                    color={opened ? 'orange' : (label ? 'gray' : 'black')}
                    size={label ? "compact-xs" : "xs"}
                    variant={opened ? 'filled' : (label ? 'default' : 'subtle')}
                    leftSection={label ? <IconEdit size={11} stroke={1.8} /> : undefined}
                    onClick={() => setOpened(true)}
                    className={label ? "text-[10px] font-medium px-1.5 h-5" : undefined}
                >
                    {label || <IconEdit size={14} stroke={1.5} />}
                </Button>
            </Tooltip>

            <Modal
                opened={opened}
                onClose={() => setOpened(false)}
                title={t('edit_cost')}
                size="lg"
                radius="lg"
                centered
            >
                <div className="space-y-2">

                    {/* Provider Selection */}
                    {service?.providers && service.providers.length > 0 && (
                        <div className="solid-box-border-positive">
                            <Switch
                                checked={applyToAllProviders}
                                onChange={(event) => {
                                    const checked = event.currentTarget.checked;
                                    setApplyToAllProviders(checked);
                                    if (checked) {
                                        // Switch ON = All providers (null)
                                        setDraft({ ...draft, business_id: null });
                                    }
                                }}
                                label={
                                    <span className="font-semibold">
                                        {applyToAllProviders ? t('all_providers') : t('provider_specific_cost')}
                                    </span>
                                }
                                description={
                                    applyToAllProviders
                                        ? t('this_cost_applies_to_all_providers')
                                        : null
                                }
                                color="green"
                                size="md"
                            />

                            {!applyToAllProviders && (
                                <div className="mt-3">
                                    <Select
                                        placeholder={t('select_provider')}
                                        leftSection={
                                            selectedBusiness?.category
                                                ? <TagIcon name={selectedBusiness.category} size="xxs" hideText />
                                                : <IconBuildingStore size={16} stroke={1.5} />
                                        }
                                        value={draft?.business_id?.toString()}
                                        data={[
                                            ...service?.providers?.map((provider: Business) => ({
                                                value: provider.id.toString(),
                                                label: provider.name
                                            }))
                                        ]}
                                        onChange={(value) => setDraft(
                                            { ...draft, business_id: value ? Number(value) : null }
                                        )}
                                        searchable
                                        required
                                        renderOption={renderProviderOption}
                                    />
                                </div>
                            )}
                        </div>
                    )}

                    {/* Date Inputs - Separated start_day and end_day */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <DatePickerInput
                            label={t('start_date')}
                            placeholder={t('select_date')}
                            leftSection={<IconCalendar size={16} stroke={1.5} />}
                            value={draft.start_day ? dayjs(draft.start_day).toDate() : null}
                            onChange={(date) => {
                                const formatted = date ? dayjs(date).format('YYYY-MM-DD') : '';
                                setDraft((prev) => ({
                                    ...prev,
                                    start_day: formatted,
                                    end_day: prev.end_day && formatted && dayjs(prev.end_day).isBefore(dayjs(formatted), 'day') ? '' : prev.end_day,
                                }));
                            }}
                            clearable
                            valueFormat="DD MMMM, YYYY"
                            locale={i18n.language}
                            popoverProps={{ withinPortal: true }}
                        />
                        <DatePickerInput
                            label={t('end_date')}
                            placeholder={t('select_date')}
                            leftSection={<IconCalendar size={16} stroke={1.5} />}
                            value={draft.end_day ? dayjs(draft.end_day).toDate() : null}
                            minDate={draft.start_day ? dayjs(draft.start_day).toDate() : undefined}
                            onChange={(date) => {
                                setDraft((prev) => ({
                                    ...prev,
                                    end_day: date ? dayjs(date).format('YYYY-MM-DD') : '',
                                }));
                            }}
                            clearable
                            valueFormat="DD MMMM, YYYY"
                            locale={i18n.language}
                            popoverProps={{ withinPortal: true }}
                        />
                    </div>


                    {/* Cost Inputs */}
                    <div className="solid-box-border-dark space-y-2">
                        <CurrencySelector
                            value={draft?.default_currency || DEFAULT_CURRENCY}
                            onChange={currency => setDraft(prev => ({
                                ...prev,
                                default_currency: currency,
                                child_currency: currency,
                                senior_currency: currency,
                                infant_currency: currency,
                            }))}
                        />
                        <div className="flex gap-2">
                            <div className={costClassName}>
                                <MoneyNumberInput
                                    className="flex-1"
                                    amount={draft?.default_amount}
                                    onChange={amount => setDraft(prev => ({ ...prev, default_amount: amount }))}
                                    label={t('default')}
                                />
                            </div>

                            <div className={costClassName}>
                                <MoneyNumberInput
                                    className="flex-1"
                                    amount={draft?.child_amount}
                                    onChange={amount => setDraft(prev => ({ ...prev, child_amount: amount }))}
                                    label={t('children')}
                                />
                            </div>
                            <div className={costClassName}>
                                <MoneyNumberInput
                                    className="flex-1"
                                    amount={draft?.senior_amount}
                                    onChange={amount => setDraft(prev => ({ ...prev, senior_amount: amount }))}
                                    label={service.show_retired ? t('retired_plural') : t('seniors')}
                                />
                            </div>
                            <div className={costClassName}>
                                <MoneyNumberInput
                                    className="flex-1"
                                    amount={draft?.infant_amount}
                                    onChange={amount => setDraft(prev => ({ ...prev, infant_amount: amount }))}
                                    label={t('infants')}
                                />
                            </div>
                        </div>
                    </div>

                    {showForeignerCost ? (
                        <div className="solid-box-border-blue space-y-2">
                            <div>
                                {t('foreigner_cost')}
                            </div>
                            <CurrencySelector
                                value={draft?.foreigner_currency || draft?.default_currency || DEFAULT_CURRENCY}
                                onChange={currency => setDraft(prev => ({
                                    ...prev,
                                    foreigner_currency: currency,
                                }))}
                            />
                            <MoneyNumberInput
                                className="flex-1"
                                amount={draft?.foreigner_amount}
                                onChange={amount => setDraft(prev => ({ ...prev, foreigner_amount: amount }))}
                                label={t('cost') + ' * ' + t('foreigner')}
                            />
                        </div>
                    ) : (
                        <Button
                            variant="light"
                            color="gray"
                            size="xs"
                            leftSection={<IconPlus size={14} />}
                            onClick={() => setShowForeignerCost(true)}
                        >
                            {t('enter_different_foreigner_cost')}
                        </Button>
                    )}
                    <div className="flex justify-between gap-2">
                        <Button
                            color="black"
                            variant="subtle"
                            onClick={() => setOpened(false)}
                        >
                            {t('close')}
                        </Button>
                        <Button
                            color="black"
                            loading={createCostMutation.isPending}
                            onClick={save}
                            radius="xl"
                            size="xs"
                            disabled={!draft?.start_day || !draft?.end_day || !draft?.default_amount}
                        >
                            {t('save')}
                        </Button>
                    </div>

                </div>
            </Modal>
        </>
    );
}